import React, { useEffect, useState, useCallback } from "react";
import { Badge, CircularProgress, Grid2, useMediaQuery } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
//styles
import styles from "./NotificationStyles.module.scss";
import SlidingPane from "react-sliding-pane";
import { Actions } from "../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../Utils/storeUtils";
import NoData from "../../../../../GlobalComponents/NoData/NoData";
import NotificationText from "./Components/NotificationText/NotificationText";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

function Notifications({ notifications = {} }) {
  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreNewNotifications, setHasMoreNewNotifications] = useState(true);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const seenNewNotificationIds = React.useRef(new Set());

  const notification = useSelector((state) => state.users.notifications);

  useEffect(() => {
    document.body.style.overflow = isPaneOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isPaneOpen]);

  const hasMoreNotifications = () => {
    const totalNotificationsCount =
      notification?.newNotificationsCount + notification?.oldNotificationsCount;
    return allNotifications?.length < totalNotificationsCount;
  };

  const fetchMoreNotifications = () => {
    const totalNotificationsCount =
      notification?.newNotificationsCount + notification?.oldNotificationsCount;
    const remainingNotifications =
      totalNotificationsCount -
      (notification.newNotifications?.length +
        notification.oldNotifications?.length);

    if (remainingNotifications <= 0) {
      setHasMoreNewNotifications(false);
      return;
    }

    setLoadingMore(true);

    getStore().dispatch({
      type: Actions.API_GET_USER_NOTIFICATIONS,
      page: currentPage + 1, // Fetch from next page
      meta: {
        onSuccess: (response) => {
          const { newNotifications, oldNotifications } = response;
          if (
            newNotifications?.length + oldNotifications?.length <
            totalNotificationsCount
          ) {
            setCurrentPage(currentPage + 1); // Update current page
          } else {
            setHasMoreNewNotifications(false); // Disable further loading
          }
          setLoadingMore(false);
        },
        onError: (error) => {
          console.error("Error fetching notifications:", error);
          setLoadingMore(false);
        },
      },
    });
  };

  const handleNotificationScroll = useCallback(
    (event) => {
      const element = event.target;
      const isAtBottom =
        element.scrollHeight - element.scrollTop <= element.clientHeight + 1;

      if (isAtBottom && !loadingMore && hasMoreNotifications()) {
        fetchMoreNotifications();
      }
    },
    [loadingMore, hasMoreNotifications]
  );

  useEffect(() => {
    const updateNotifications = () => {
      const totalNotificationsCount =
        notification?.newNotificationsCount +
        notification?.oldNotificationsCount;
      setHasMoreNewNotifications(
        allNotifications?.length < totalNotificationsCount
      );
    };
    updateNotifications();
  }, [
    notification?.newNotifications,
    allNotifications,
    notification?.totalNotificationsCount,
  ]);

  useEffect(() => {
    if (notification?.newNotifications?.length > 0) {
      notification.newNotifications.forEach((n) =>
        seenNewNotificationIds.current.add(n.id)
      );
    }
  }, [notification?.newNotifications]);

  useEffect(() => {
    return () => {
      // Mark all new notifications as read when the slider is closed
      if (isPaneOpen && seenNewNotificationIds.current.size > 0) {
        markNotificationsAsRead([...seenNewNotificationIds.current]);
        seenNewNotificationIds.current = new Set();
      }
    };
  }, [isPaneOpen]);

  const markNotificationsAsRead = (notificationIds) => {
    getStore().dispatch({
      type: Actions.API_MARK_NOTIFICATIONS_READ,
      data: { selectedNotificationBodies: notificationIds },
      meta: {
        onSuccess: () => {
          getStore().dispatch({
            type: Actions.API_GET_USER_NOTIFICATIONS,
            page: currentPage,
            meta: {
              onSuccess: (response) => {
                const {
                  newNotifications,
                  oldNotifications,
                  totalNotificationsCount,
                } = response;
                setAllNotifications((prevNotifications) => [
                  ...prevNotifications,
                  ...newNotifications,
                  ...oldNotifications,
                ]);
                if (
                  newNotifications.length + oldNotifications.length <
                  totalNotificationsCount
                ) {
                  setCurrentPage(currentPage + 1);
                } else {
                  setHasMoreNewNotifications(false);
                }
              },
              onError: (error) => {
                console.error("Error fetching notifications:", error);
              },
            },
          });
        },
        onError: (error) => {
          console.error("Error marking notifications as read:", error);
        },
      },
    });
  };

  return (
    <>
      <Grid2
        className={styles.Notification}
        onClick={() => setIsPaneOpen(true)}
      >
        <Badge
          invisible={isPaneOpen}
          color="error"
          overlap="circular"
          badgeContent={
            notification && notification?.totalNotificationsCount
              ? notification?.totalNotificationsCount
              : 0
          }
          sx={{
            "& .MuiBadge-badge": {
              minWidth: "14px",
              height: "14px",
              fontSize: "10px",
              padding: "0 4px",
              marginRight: "4px",
            },
          }}
        >
          <NotificationsNoneOutlinedIcon className={styles.Notificon} />
        </Badge>
      </Grid2>
      <SlidingPane
        isOpen={isPaneOpen}
        title="Notifications"
        from={isMobile ? "bottom" : "right"}
        width={isMobile ? "100%" : "26%"}
        onRequestClose={() => setIsPaneOpen(false)}
        className={`${styles.slidePane} padding`}
        hideHeader
      >
        <Grid2 className={styles.Container} onScroll={handleNotificationScroll}>
          <Grid2 className={styles.lineHeader}>
            <Grid2 className={styles.header}>Notifications</Grid2>
            <Grid2 onClick={() => setIsPaneOpen(false)}>
              <CloseIcon className={styles.CloseIcon} />
            </Grid2>
          </Grid2>
          <Grid2 className={styles.allNotifications}>
            {notification?.newNotifications?.length > 0 ||
            notification?.oldNotifications?.length > 0 ? (
              <>
                {notification?.newNotifications.map((notif) => (
                  <Grid2 key={notif.id} className={styles.flexBox}>
                    <Grid2 className={styles.NotificationText}>
                      <NotificationText
                        header={notif.header}
                        body={notif.body}
                        read={true}
                        createdAt={notif.createdTs}
                        isSelected={notif.read}
                      />
                    </Grid2>
                  </Grid2>
                ))}
                {notification?.oldNotifications.map((notif) => (
                  <Grid2 key={notif.id} className={styles.flexBox}>
                    <Grid2 className={styles.NotificationText}>
                      <NotificationText
                        header={notif.header}
                        body={notif.body}
                        read={true}
                        createdAt={notif.createdTs}
                        isSelected={notif.read}
                      />
                    </Grid2>
                  </Grid2>
                ))}
                <Grid2 className={styles.loadingState}>
                  {loadingMore && <CircularProgress />}
                </Grid2>
              </>
            ) : (
              <Grid2 className={styles.noData}>
                <NoData message={"No Notifications"} />
              </Grid2>
            )}
          </Grid2>
        </Grid2>
      </SlidingPane>
    </>
  );
}

export default Notifications;
